import React from 'react';
import classnames from 'classnames';
import ErrorBoundary from '@wix/da-react-context/pkg/ErrorBoundary';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import ConditionalWrapper from '@wix/da-shared-react/pkg/ConditionalWrapper';
import { hashString } from '@wix/da-shared-react/pkg/utils/hash';
import { PapiUser } from '@wix/da-papi-types';
import { WithSectionContextProps } from '../../Sections/SectionContext';
import { ModuleType } from '../../../../types/modules';
import { ZoneType, InstallableModuleType } from '../../../../types/layout';
import WidgetPaywall from '../../UserWidgets/_partials/paywalls/WidgetPaywall';

import AddWidget from '../AddWidget';
import s from './Zone.scss';

export interface Props extends WithSectionContextProps {
  type: ZoneType;
  locked?: boolean;
  isRemovable?: boolean;
  profileOwnerUser: PapiUser;
  isOwner: boolean;
  isAuthorizedToAddModule: boolean;
  isAuthorizedToMoveModule: boolean;
  isMobile: boolean;
  showCoreUpsell: boolean;
  installableModuleTypes: ModuleType[];
  installableModulesInfo: InstallableModuleType[];
  installedModuleIds: number[];
  installedComponents: {
    moduleId: number;
    moduleType: ModuleType;
    ComponentClass: React.ComponentType<any>;
  }[];
  mapModuleTypeToWidgetComponent: {
    [moduleName: string]: React.ComponentType<any>;
  };
  isInstallingModule?: boolean;
  currentlyInstalledModulePostion?: number;
  className?: string;
  headerClassName?: string;
  moveModule: (moduleId: number, position: number) => void;
  removeModule: (moduleId: number) => void;
  addModule: (moduleType: ModuleType, position: number) => void;
  children?: React.ReactNode;
  direction?: 'row' | 'column';
  withPaywall?: boolean;
}

const defaultProps = {
  installableModuleTypes: [],
  installedModuleIds: [],
  locked: false,
  isRemovable: true,
};

class Zone extends React.PureComponent<Props> {
  static defaultProps = defaultProps;
  render() {
    const {
      section,
      className,
      type: zone,
      installedComponents,
      installableModulesInfo,
      addModule,
      moveModule,
      removeModule,
      isRemovable,
      locked,
      isInstallingModule,
      currentlyInstalledModulePostion,
      isOwner,
      isAuthorizedToMoveModule,
      profileOwnerUser,
      headerClassName,
      direction = 'column',
      withPaywall = false,
      showCoreUpsell,
    } = this.props;

    return (
      <ConditionalWrapper
        condition={
          withPaywall && showCoreUpsell && installedComponents.length > 0
        }
        wrap={child => <WidgetPaywall>{child}</WidgetPaywall>}
      >
        <div
          className={classnames(s['root'], className, s[direction])}
          key={zone}
        >
          {installedComponents.map((installedComponent, idx, modules) => {
            const { moduleId, moduleType, ComponentClass } = installedComponent;
            const moduleInfo = installableModulesInfo.find(
              info => info.moduleName === moduleType
            );
            const isFirst = idx === 0;
            const isLast = idx === modules.length - 1;
            const canMoveUp = !locked && !isFirst && isAuthorizedToMoveModule;
            const canMoveDown = !locked && !isLast && isAuthorizedToMoveModule;
            const canRemove = !locked && isRemovable;
            const props = {
              key: idx,
              moduleId,
              widgetMoveUp: canMoveUp
                ? moveModule(moduleId, idx - 1)
                : undefined,
              widgetMoveDown: canMoveDown
                ? moveModule(moduleId, idx + 1)
                : undefined,
              widgetRemove: canRemove ? removeModule(moduleId) : undefined,
              widgetAddColumn:
                direction === 'row' && isLast && moduleInfo
                  ? () => addModule(moduleType, idx + 1)
                  : undefined,
              isOwner,
              profileOwnerUser,
              headerClassName,
              section,
              zone,
            };
            return (
              <ErrorBoundary debugComponent="Zone" key={idx}>
                {isInstallingModule &&
                  idx === currentlyInstalledModulePostion &&
                  this.renderLoadingIndicator()}
                <ComponentClass
                  {...props}
                  widgetBoxClassName={s['item']}
                  position={idx}
                  direction={direction}
                />
                {!isLast && this.renderAddWidgetIfAllowed(idx + 1)}
              </ErrorBoundary>
            );
          })}
        </div>
      </ConditionalWrapper>
    );
  }

  renderLoadingIndicator() {
    return <LoadingIndicator view="daSymbolPurple" />;
  }

  renderAddWidgetIfAllowed(position) {
    const {
      type,
      locked,
      isInstallingModule,
      installableModuleTypes,
      isOwner,
      isAuthorizedToAddModule,
      isMobile,
      addModule,
      section,
      direction = 'column',
    } = this.props;
    if (direction === 'row') {
      // row zones should not show the addModule widget
      return null;
    }

    const isAuthorized = isOwner || isAuthorizedToAddModule;

    if (
      locked ||
      isMobile ||
      !isAuthorized ||
      !installableModuleTypes ||
      !installableModuleTypes.length
    ) {
      return null;
    }
    const installableModules = installableModuleTypes.map(moduleName => ({
      zone: type,
      moduleName,
      position,
    }));

    const uniqueKey = hashString(
      installableModules
        .reduce(
          (result, { zone, moduleName, position: pos }) => [
            ...result,
            `${zone}.${moduleName}.${position}.${pos}`,
          ],
          []
        )
        .join(':')
    );

    return (
      <AddWidget
        section={section}
        position="zone"
        key={`add-widget-${uniqueKey}`}
        addModule={(zone, moduleName, modulePosition) =>
          addModule(moduleName, modulePosition)
        }
        installableModules={installableModules}
        disabled={isInstallingModule}
      />
    );
  }
}

export default Zone;
